import { Progress } from '@components/Progress'
import { useCourse } from '@hooks/useCourse'
import { useEnrollment } from '@hooks/useEnrollment'
import { checkProgress, progressColor } from '@utils/courseProgress'
import { useTranslation } from 'react-i18next'
import { Text, XStack, YStack } from 'tamagui'

/**
 * Faixa compacta de progresso da home focada (FR-016b): os mesmos números da
 * tela padrão do curso, via `utils/courseProgress`. O certificado fica no menu.
 * `inline` é a versão que vai na linha do título do Continue (desktop web).
 */
export function CourseProgressStrip({ inline = false }: { inline?: boolean }) {
  const { course } = useCourse()
  const { enrollment } = useEnrollment()
  const { t } = useTranslation('FeaturedProductHome')

  if (!course || !enrollment?.progressCard) return null

  const { progress, completed, total } = enrollment.progressCard

  return (
    <XStack
      testID="course-progress-strip"
      alignItems="center"
      justifyContent="space-between"
      gap="$spacing-inset-xs"
      {...(!inline && {
        paddingHorizontal: '$size.spacing-xs',
        $gtSm: { paddingHorizontal: '$size.spacing-xsl' },
      })}
    >
      <YStack
        flex={1}
        gap="$spacing-inset-nano"
        $gtSm={{ flexDirection: 'row', alignItems: 'center' }}
        accessible
        accessibilityRole="progressbar"
        accessibilityLabel={t('progressStrip.accessibilityLabel', {
          name: course.name,
          completed,
          total,
        })}
      >
        <Text
          color="$text-secondary"
          ff="$mulishRegular"
          fontSize="$font-size-xxxs"
          $gtSm={{ fontSize: '$font-size-xxs', flexShrink: 1 }}
          numberOfLines={1}
        >
          {course.name}
        </Text>
        <XStack alignItems="center" gap="$spacing-inset-xxs">
          <Progress
            value={progress}
            height={4}
            foregroundColor={progressColor(progress)}
            $gtSm={{ width: 160, flex: 0 }}
          />
          <Text
            flexShrink={0}
            color="$text-secondary"
            ff="$mulishRegular"
            fontSize="$font-size-xxxs"
            $gtSm={{ fontSize: '$font-size-xxs' }}
            numberOfLines={1}
          >
            {t('progressStrip.summary', {
              completed,
              total,
              progress: checkProgress(progress),
            })}
          </Text>
        </XStack>
      </YStack>
    </XStack>
  )
}
